import type { AnalyticsAdapter, AnalyticsProps } from "./AnalyticsAdapter";
import { scrubSentryProps, sentryPrivacyOptions, type SentryPrivacyOptions } from "./sentryPrivacy";

interface SentryCaptureContext {
  level?: string;
  extra?: Record<string, unknown>;
}

interface SentryInitOptions extends SentryPrivacyOptions {
  dsn: string;
  integrations?: unknown[] | ((defaults: SentryIntegration[]) => SentryIntegration[]);
  release?: string;
  environment?: string;
}

interface SentryClient {
  init(options: SentryInitOptions): void;
  captureMessage(message: string, context?: SentryCaptureContext): void;
  setUser(user: { id: string }): void;
  addBreadcrumb(crumb: { category?: string; message: string; data?: AnalyticsProps }): void;
}

export interface SentryIntegration {
  name: string;
}

interface SentryModule {
  breadcrumbsIntegration?: (options: { console: boolean; dom: boolean }) => unknown;
}

function isErrorEvent(event: string): boolean {
  return event.includes("error") || event.includes("failed");
}

/**
 * Replaces the default Breadcrumbs integration with one that never records
 * console output or DOM (`ui.*`) interactions, the two breadcrumb sources whose
 * free-text content is most likely to carry personal data. Merged with (not
 * replacing) the other default integrations. Fails closed: when the SDK no
 * longer exposes the integration factory, the default Breadcrumbs integration
 * is removed instead of being left enabled with console/DOM capture on.
 */
export function privacyIntegrations(mod: SentryModule): {
  integrations: unknown[] | ((defaults: SentryIntegration[]) => SentryIntegration[]);
} {
  const breadcrumbs = mod.breadcrumbsIntegration;
  if (typeof breadcrumbs !== "function") {
    return {
      integrations: (defaults) => defaults.filter((entry) => entry.name !== "Breadcrumbs"),
    };
  }
  return { integrations: [breadcrumbs({ console: false, dom: false })] };
}

/**
 * Sentry destination for error/diagnostic signals. Loaded with a guarded
 * dynamic import only when init() runs (env DSN present). Initialised with
 * privacy-hardened options (see {@link sentryPrivacyOptions}): no default PII,
 * scrubbers that strip the request block, user IP, fingerprinting contexts and
 * breadcrumb URLs, and console/DOM breadcrumb capture disabled (see
 * {@link privacyIntegrations}). Error-shaped events become captured messages
 * carrying their scrubbed diagnostics as `extra`; everything else is recorded as
 * a breadcrumb for context. Silent no-op before/without a loaded client.
 */
export class SentryDestination implements AnalyticsAdapter {
  private client: SentryClient | null = null;
  private readonly dsn: string;
  private readonly release?: string;
  private readonly environment?: string;

  constructor(config: { dsn: string; release?: string; environment?: string }) {
    this.dsn = config.dsn;
    this.release = config.release;
    this.environment = config.environment;
  }

  async init(): Promise<void> {
    try {
      const mod = await import("@sentry/browser");
      const client = mod as unknown as SentryClient;
      client.init({
        dsn: this.dsn,
        ...(this.release ? { release: this.release } : {}),
        ...(this.environment ? { environment: this.environment } : {}),
        ...sentryPrivacyOptions(),
        ...privacyIntegrations(mod as unknown as SentryModule),
      });
      this.client = client;
    } catch (error) {
      if (typeof console !== "undefined") {
        console.warn("[analytics] sentry init failed", error);
      }
    }
  }

  track(event: string, props?: AnalyticsProps): void {
    if (!this.client) {
      return;
    }
    if (isErrorEvent(event)) {
      this.client.captureMessage(event, {
        level: "error",
        ...(props ? { extra: scrubSentryProps(props) } : {}),
      });
    } else {
      this.client.addBreadcrumb({ category: "analytics", message: event, data: props });
    }
  }

  identify(anonId: string): void {
    this.client?.setUser({ id: anonId });
  }

  setUserProps(): void {
    return;
  }

  flush(): void {
    return;
  }

  optOut(): void {
    this.client = null;
  }

  reset(): void {
    return;
  }
}
